import React from "react";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import NotificationsActive from "@material-ui/icons/NotificationsActive";
import Close from "@material-ui/icons/Close";
import styled from "styled-components";
import sanitizeHtml from "../../../utils/sanitizeHtml";

export type BroadcastPreviewProps = {
	open: boolean;
	onClose: () => void;
	title: string;
	bodyHtml: string;
	displayMode: "popup" | "floating_button";
};

const InfoBanner = styled.div`
	background: #eff6ff;
	border: 1px solid #bfdbfe;
	border-radius: 8px;
	padding: 8px 12px;
	margin-bottom: 12px;
	font-size: 12px;
	color: #1e40af;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	flex-wrap: wrap;
`;

const DisplayModeBadge = styled.span`
	background: #dbeafe;
	color: #1d4ed8;
	border-radius: 4px;
	padding: 2px 8px;
	font-size: 11px;
	font-weight: 600;
	white-space: nowrap;
`;

const HeaderRow = styled.div`
	background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
	border-bottom: 1px solid #e2e8f0;
	padding: 16px 20px 14px;
	display: flex;
	align-items: flex-start;
	gap: 14px;
`;

const IconFrame = styled.div`
	width: 48px;
	height: 48px;
	border-radius: 14px;
	background: linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
`;

const HeaderText = styled.div`
	flex: 1;
	min-width: 0;
`;

const PreviewTitle = styled.div`
	font-size: 18px;
	font-weight: 600;
	color: #0f172a;
	line-height: 1.3;
	word-break: break-word;
`;

const PreviewSubtitle = styled.div`
	font-size: 13px;
	color: #64748b;
	margin-top: 3px;
	line-height: 1.4;
`;

const BodyArea = styled.div`
	padding: 20px;
	min-height: 80px;
`;

const BodyContent = styled.div`
	border-left: 3px solid #3b82f6;
	padding-left: 16px;
	font-size: 15px;
	line-height: 1.65;
	color: #1e293b;
	word-break: break-word;

	p {
		margin: 0 0 0.6em;
	}
	p:last-child {
		margin-bottom: 0;
	}
`;

const ActionBar = styled.div`
	background: #f8fafc;
	border-top: 1px solid #e2e8f0;
	padding: 14px 20px;
	display: flex;
	justify-content: flex-end;
`;

const displayModeLabel = (d: string) => (d === "floating_button" ? "Nút thông báo nổi" : "Popup (một lần mỗi phiên)");

export default function BroadcastPreview(props: BroadcastPreviewProps) {
	const { open, onClose, title, bodyHtml, displayMode } = props;

	const resolvedTitle = title && title.trim() ? title.trim() : "Thông báo từ WhaleLO";

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			disableEnforceFocus
			disableAutoFocus
			PaperProps={{
				style: {
					borderRadius: 18,
					overflow: "hidden",
				},
			}}>
			<DialogTitle style={{ padding: "16px 20px 0", background: "#ffffff" }}>
				<InfoBanner>
					<span>Preview — Đây là cách thông báo sẽ hiển thị trên Station portal</span>
					<DisplayModeBadge>{displayModeLabel(displayMode)}</DisplayModeBadge>
				</InfoBanner>
				<IconButton
					aria-label="close preview"
					size="small"
					style={{ position: "absolute", right: 10, top: 10, color: "#64748b" }}
					onClick={onClose}>
					<Close fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent style={{ padding: 0, background: "#ffffff" }}>
				<div
					style={{
						border: "1px solid #e2e8f0",
						borderRadius: 12,
						margin: "0 16px 16px",
						overflow: "hidden",
					}}>
					<HeaderRow>
						<IconFrame>
							<NotificationsActive style={{ fontSize: 22, color: "#2563eb" }} />
						</IconFrame>
						<HeaderText>
							<PreviewTitle>{resolvedTitle}</PreviewTitle>
							<PreviewSubtitle>WhaleLO có thông báo dành cho bạn</PreviewSubtitle>
						</HeaderText>
					</HeaderRow>

					<BodyArea>
						<BodyContent
							{...(bodyHtml && bodyHtml.trim()
								? {
										dangerouslySetInnerHTML: {
											// nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
											__html: sanitizeHtml(bodyHtml),
										},
								  }
								: {
										children: (
											<span style={{ color: "#94a3b8", fontStyle: "italic" }}>
												Chưa có nội dung.
											</span>
										),
								  })}
						/>
					</BodyArea>

					<ActionBar>
						<Button
							variant="contained"
							color="primary"
							onClick={onClose}
							style={{ borderRadius: 10, fontWeight: 600, textTransform: "none" }}>
							Đã hiểu
						</Button>
					</ActionBar>
				</div>
			</DialogContent>
		</Dialog>
	);
}
